import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import moment, { Moment } from "moment";
import { makeStyles } from "tss-react/mui";
import z from "zod";
import {
  MiAbbreviationBadge,
  MiCard,
  MiControlledSelectBox,
  MiControlledTextField,
  MiDatePicker,
  MiInputTextbox,
  MiTimePicker,
} from "@miplatform/components";
import { DATE_TIME_FORMATS } from "@miplatform/constants";
import { MiTHEME } from "@miplatform/theme";
import { IHome } from "@miplatform/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiCalendarBlankOutline, mdiFileDocumentOutline } from "@mdi/js";
import { Footer } from "@mibuildweb/components";
import { useOrders } from "@mibuildweb/hooks";
import { Box, Divider, MenuItem, Typography } from "@mui/material";
import {
  CreateCustomWorkOrderForm,
  CustomWorkTemplateBase,
} from "./CreateWorkOrderDrawer.constants";

interface CustomWorkOrderCreationProps {
  home: IHome;
  template: CustomWorkTemplateBase;
  isLoading?: boolean;
  onSubmit: (data: CreateCustomWorkOrderForm) => void;
  onClose: () => void;
}

export const CustomWorkOrderCreation: React.FC<
  CustomWorkOrderCreationProps
> = ({ home, template, isLoading = false, onClose, onSubmit }) => {
  const { theme, classes } = useStyles();
  const {
    watch,
    setValue,
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<CreateCustomWorkOrderForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      orderId: "",
      workOrderName: "",
      workOrderAbbreviation: "",
      workOrderDuration: "",
      color: MiTHEME.palette.grey.medium,
      dueDate: null,
      dueDateTime: null,
      notes: "",
    },
  });

  const { orders, getOrders } = useOrders();
  const orderOptions = useMemo(
    () =>
      orders.map((order) => ({
        value: order.id,
        label: order.orderNumber,
        ...order,
      })),
    [orders]
  );
  const {
    dueDate,
    dueDateTime,
    workOrderDuration,
    workOrderName,
    workOrderAbbreviation,
  } = watch();
  const handleDueDateChange = (date: Moment | null) =>
    setValue("dueDate", date, { shouldValidate: true, shouldDirty: true });

  const handleDueDateTimeChange = (date: Moment | null) =>
    setValue("dueDateTime", date, { shouldValidate: true, shouldDirty: true });

  const handleDurationChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setValue("workOrderDuration", event.target.value, {
      shouldValidate: true,
      shouldDirty: true,
    });

  useEffect(() => {
    getOrders({ filters: { homeId: { type: "guid", value: home.id } } });
  }, [home.id]);

  return (
    <Box className={classes.root}>
      <Box flex={1}>
        <MiCard
          containerStyles={{
            display: "flex",
            flexDirection: "column",
            padding: theme.spacing(0, 4),
            margin: theme.spacing(0, 3),
            gap: theme.spacing(3),
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <MiAbbreviationBadge
              abbreviation={template.abbreviation as string}
              backgroundColor={template.color as string}
            />
            <Typography fontWeight={theme.fontWeight.semiBold}>
              {template.name}
            </Typography>
          </Box>
          <MiCard
            leftIcon={mdiCalendarBlankOutline}
            title="Attached Order"
            containerStyles={{}}
          >
            <MiControlledSelectBox
              required
              label="Attached Order"
              name="orderId"
              whiteBackground
              disableNone
              formControl={control}
              options={orderOptions}
              renderOption={(option: (typeof orderOptions)[number]) => (
                <MenuItem
                  key={option.id}
                  id={option.id}
                  value={option.value}
                  className={classes.menuItem}
                >
                  <Box display="flex" alignItems="center" gap={2} flex={1}>
                    <MiAbbreviationBadge
                      abbreviation={option.orderTemplateAbbreviation as string}
                      backgroundColor={option.orderTemplateColor as string}
                    />

                    <Typography noWrap maxWidth={150}>
                      {option.home?.address?.addressLine1}
                    </Typography>
                    <Divider orientation="vertical" flexItem />
                    <Typography color="grey.medium">
                      #{option.orderNumber}
                    </Typography>
                  </Box>
                </MenuItem>
              )}
            />
          </MiCard>
          <MiCard
            leftIcon={mdiCalendarBlankOutline}
            title="Work Order Details"
            containerStyles={{}}
          >
            <Box display="flex" gap={2}>
              <MiInputTextbox
                required
                label="Work Order Name"
                name="workOrderName"
                value={workOrderName}
                onChange={(e) =>
                  setValue("workOrderName", e.target.value, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }
                error={errors.workOrderName as { message: string } | undefined}
                sx={{ backgroundColor: theme.palette.background.default }}
              />
              <MiInputTextbox
                required
                label="Work Order Abbreviation"
                name="workOrderAbbreviation"
                value={workOrderAbbreviation}
                maxLength={3}
                onChange={(e) =>
                  setValue("workOrderAbbreviation", e.target.value, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }
                error={
                  errors.workOrderAbbreviation as
                    | { message: string }
                    | undefined
                }
                sx={{ backgroundColor: theme.palette.background.default }}
              />
            </Box>
          </MiCard>
          <MiCard
            leftIcon={mdiCalendarBlankOutline}
            title="Date Information"
            containerStyles={{}}
          >
            <Box display="flex" gap={2}>
              <Box width="100%">
                <MiDatePicker
                  required
                  whiteBackground
                  label="Date"
                  value={dueDate}
                  format={DATE_TIME_FORMATS.FORMAT_MMMM_D_YYYY}
                  error={errors.dueDate?.message}
                  onChange={handleDueDateChange}
                />
              </Box>
              <Box width="100%">
                <MiTimePicker
                  required
                  label="Due Time"
                  value={dueDateTime}
                  format={DATE_TIME_FORMATS.HOURS_MINUTES}
                  error={errors.dueDateTime as { message: string } | undefined}
                  sx={{ backgroundColor: theme.palette.background.default }}
                  onChange={handleDueDateTimeChange}
                />
              </Box>
              <Box width="100%">
                <NumericFormat
                  required
                  label="Work Order Duration (Min)"
                  customInput={MiInputTextbox}
                  allowNegative={false}
                  value={workOrderDuration}
                  error={
                    errors.workOrderDuration as { message: string } | undefined
                  }
                  sx={{ backgroundColor: theme.palette.background.default }}
                  onChange={handleDurationChange}
                />
              </Box>
            </Box>
          </MiCard>
          <MiCard
            leftIcon={mdiFileDocumentOutline}
            title="Notes"
            containerStyles={{
              paddingBottom: theme.spacing(5),
            }}
          >
            <MiControlledTextField
              name="notes"
              label="Notes"
              formControl={control}
              sx={{ backgroundColor: theme.palette.background.default }}
            />
          </MiCard>
        </MiCard>
      </Box>
      <Footer
        show
        saveTitle="Save and Create Work Order"
        disableSave={isLoading || !isValid}
        handleCancelClick={onClose}
        handleSaveClick={handleSubmit(onSubmit)}
      />
    </Box>
  );
};

const momentSchema = z.custom<Moment | null>(
  (val) => val === null || moment.isMoment(val),
  { message: "Invalid date format" }
);

const schema = z
  .object({
    dueDate: momentSchema.refine((val) => val !== null, {
      message: "Due date is required",
    }),
    dueDateTime: momentSchema,
    workOrderName: z.string().min(1, "Name is required"),
    workOrderAbbreviation: z
      .string()
      .min(1, "Abbreviation is required")
      .max(3, "Abbreviation cannot exceed 3 characters"),
    orderId: z.string().min(1, "Attached Order is required"),
    workOrderDuration: z.string().min(1, "Duration is required"),
    notes: z
      .string()
      .max(500, "Notes cannot exceed 500 characters")
      .optional()
      .default(""),
  })
  .refine(
    (data) => {
      return data.dueDateTime?.isValid();
    },
    {
      message: "Due time is required",
      path: ["dueDateTime"],
    }
  );

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    flex: 1,
    height: "100%",
    paddingTop: theme.spacing(2),
  },
  menuItem: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
}));
